import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useMemo, useState } from 'react';
import { getGoogleWorkspaceStatus, scheduleGoogleCalendarMeeting } from '../api/integrations';
import { createTask, fetchContacts, fetchTasks, updateTask, type Task } from '../api/resources';
import { useAuth } from '../context/AuthContext';

const PAYING_ROLES = new Set(['admin', 'owner', 'pro', 'enterprise']);

type ActionMessage = {
  type: 'success' | 'error';
  text: string;
};

function toLocalInputValue(date: Date) {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatTaskDue(task: Task) {
  if (!task.due_date) return '—';
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };
  if (task.due_time !== null) {
    options.timeStyle = 'short';
  }
  return new Date(task.due_date * 1000).toLocaleString([], options);
}

export function TasksPage() {
  const { user } = useAuth();
  const isPaying = useMemo(() => (user ? PAYING_ROLES.has(user.role) : false), [user]);
  const { data: contacts } = useQuery({ queryKey: ['contacts'], queryFn: fetchContacts });
  const { data: tasks } = useQuery({ queryKey: ['tasks'], queryFn: fetchTasks });
  const {
    data: googleStatus,
    isLoading: isGoogleStatusLoading,
  } = useQuery({
    queryKey: ['googleIntegration'],
    queryFn: getGoogleWorkspaceStatus,
    enabled: isPaying,
    staleTime: 30_000,
  });
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState({
    title: '',
    description: '',
    relatedContactId: '',
    dueDate: '',
    dueTime: '',
  });
  const [schedulerTask, setSchedulerTask] = useState<Task | null>(null);
  const [scheduleForm, setScheduleForm] = useState({
    start: '',
    durationMinutes: '30',
    attendees: '',
    notes: '',
    location: '',
  });
  const [scheduleMessage, setScheduleMessage] = useState<ActionMessage | null>(null);
  const [scheduledLink, setScheduledLink] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setFormState({ title: '', description: '', relatedContactId: '', dueDate: '', dueTime: '' });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (payload: { id: string; status: string }) => updateTask(payload.id, { status: payload.status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const scheduleMeetingMutation = useMutation({
    mutationFn: scheduleGoogleCalendarMeeting,
    onSuccess: (response) => {
      setScheduleMessage({ type: 'success', text: 'Meeting scheduled in Google Calendar.' });
      setScheduledLink(response.htmlLink);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['googleIntegration'] });
    },
    onError: (err: Error) => {
      setScheduleMessage({ type: 'error', text: err.message });
    },
  });

  const googleStatusData = googleStatus ?? null;
  const canScheduleMeetings = Boolean(
    googleStatusData?.connected &&
      googleStatusData?.canScheduleMeetings &&
      !googleStatusData?.requiresReauthForMeetings,
  );

  const showScheduleButton = Boolean(isPaying && googleStatusData?.connected);
  const scheduleDisabledReason = !googleStatusData?.connected
    ? 'Connect Google Workspace to schedule meetings'
    : googleStatusData?.requiresReauthForMeetings
    ? 'Reconnect Google Workspace to grant calendar access'
    : null;

  function openScheduler(task: Task) {
    const baseDate = task.due_date ? new Date(task.due_date * 1000) : new Date();
    const defaultAttendees = task.contact_email?.trim() ?? '';
    const defaultNotes = task.title;
    scheduleMeetingMutation.reset();
    setSchedulerTask(task);
    setScheduleForm({
      start: toLocalInputValue(baseDate),
      durationMinutes: '30',
      attendees: defaultAttendees,
      notes: defaultNotes,
      location: '',
    });
    setScheduleMessage(null);
    setScheduledLink(null);
  }

  function closeScheduler() {
    scheduleMeetingMutation.reset();
    setSchedulerTask(null);
    setScheduleMessage(null);
    setScheduledLink(null);
  }

  function handleScheduleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!schedulerTask) return;
    if (!scheduleForm.start) {
      setScheduleMessage({ type: 'error', text: 'Choose a start time.' });
      return;
    }

    const startIso = new Date(scheduleForm.start).toISOString();
    const durationMinutes = Number.parseInt(scheduleForm.durationMinutes, 10) || 30;
    const attendees = scheduleForm.attendees
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);

    setScheduleMessage(null);
    setScheduledLink(null);
    scheduleMeetingMutation.mutate({
      taskId: schedulerTask.id,
      start: startIso,
      durationMinutes,
      attendees: attendees.length ? attendees : undefined,
      notes: scheduleForm.notes.trim() ? scheduleForm.notes.trim() : undefined,
      location: scheduleForm.location.trim() ? scheduleForm.location.trim() : undefined,
      title: schedulerTask.title,
    });
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Tasks</h1>
          <p>Stay on top of every follow-up and deliverable.</p>
        </div>
      </div>

      <div className="card">
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();

            let dueDateSeconds: number | undefined;
            let dueTimeSeconds: number | undefined;

            if (formState.dueDate) {
              const iso = `${formState.dueDate}T${formState.dueTime || '00:00'}`;
              const parsed = new Date(iso);
              if (!Number.isNaN(parsed.getTime())) {
                dueDateSeconds = Math.floor(parsed.getTime() / 1000);
                if (formState.dueTime) {
                  dueTimeSeconds = (parsed.getUTCHours() * 3600) + (parsed.getUTCMinutes() * 60) + parsed.getUTCSeconds();
                }
              }
            }

            createMutation.mutate({
              title: formState.title,
              description: formState.description || undefined,
              relatedContactId: formState.relatedContactId || undefined,
              dueDate: typeof dueDateSeconds === 'number' ? dueDateSeconds : undefined,
              dueTime: typeof dueTimeSeconds === 'number' ? dueTimeSeconds : undefined,
            });
          }}
        >
          <div className="form-grid">
            <label>
              Title
              <input value={formState.title} onChange={(e) => setFormState((s) => ({ ...s, title: e.target.value }))} required />
            </label>
            <label>
              Related contact
              <select value={formState.relatedContactId} onChange={(e) => setFormState((s) => ({ ...s, relatedContactId: e.target.value }))}>
                <option value="">Optional</option>
                {contacts?.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {[contact.first_name, contact.last_name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Due date
              <input type="date" value={formState.dueDate} onChange={(e) => setFormState((s) => ({ ...s, dueDate: e.target.value }))} />
            </label>
            <label>
              Due time
              <input type="time" value={formState.dueTime} onChange={(e) => setFormState((s) => ({ ...s, dueTime: e.target.value }))} />
            </label>
          </div>
          <label>
            Description
            <textarea value={formState.description} onChange={(e) => setFormState((s) => ({ ...s, description: e.target.value }))} rows={3} />
          </label>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating…' : 'Create task'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Status</th>
              <th>Contact</th>
              <th>Due</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tasks?.map((task) => (
              <tr key={task.id}>
                <td>{task.title}</td>
                <td>{task.status}</td>
                <td>{[task.first_name, task.last_name].filter(Boolean).join(' ') || '—'}</td>
                <td>{formatTaskDue(task)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    {showScheduleButton && (
                      <button
                        className="btn btn--ghost"
                        type="button"
                        onClick={() => openScheduler(task)}
                        disabled={!canScheduleMeetings || scheduleMeetingMutation.isPending}
                        title={canScheduleMeetings ? undefined : scheduleDisabledReason ?? undefined}
                      >
                        {scheduleMeetingMutation.isPending && schedulerTask?.id === task.id ? 'Scheduling…' : 'Schedule meeting'}
                      </button>
                    )}
                    <button
                      className="btn btn--ghost"
                      type="button"
                      onClick={() =>
                        updateStatusMutation.mutate({
                          id: task.id,
                          status: task.status === 'completed' ? 'open' : 'completed',
                        })
                      }
                    >
                      Mark {task.status === 'completed' ? 'open' : 'done'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!tasks?.length && (
              <tr>
                <td colSpan={5}>No tasks yet.</td>
              </tr>
            )}
          </tbody>
        </table>
        {isPaying && googleStatusData && !isGoogleStatusLoading && !googleStatusData.connected && (
          <p className="muted" style={{ marginTop: '0.75rem' }}>
            Connect Google Workspace from the Integrations page to schedule meetings directly from tasks.
          </p>
        )}
        {isPaying && googleStatusData?.requiresReauthForMeetings && (
          <p className="form-error" style={{ marginTop: '0.75rem' }}>
            Reconnect your Google Workspace integration to re-enable meeting scheduling.
          </p>
        )}
      </div>

      {schedulerTask && (
        <div className="card">
          <div className="page-header">
            <div>
              <h2>Schedule Google Calendar meeting</h2>
              <p>{schedulerTask.title}</p>
            </div>
            <button className="btn btn--ghost" type="button" onClick={closeScheduler}>
              Close
            </button>
          </div>
          <form className="form" onSubmit={handleScheduleSubmit}>
            <div className="form-grid">
              <label>
                Start time
                <input
                  type="datetime-local"
                  value={scheduleForm.start}
                  onChange={(event) => setScheduleForm((state) => ({ ...state, start: event.target.value }))}
                  required
                />
              </label>
              <label>
                Duration (minutes)
                <input
                  type="number"
                  min={5}
                  max={1440}
                  value={scheduleForm.durationMinutes}
                  onChange={(event) => setScheduleForm((state) => ({ ...state, durationMinutes: event.target.value }))}
                  required
                />
              </label>
            </div>
            <label>
              Attendees (comma separated emails)
              <input
                type="text"
                value={scheduleForm.attendees}
                onChange={(event) => setScheduleForm((state) => ({ ...state, attendees: event.target.value }))}
                placeholder="name@example.com"
              />
            </label>
            <label>
              Location (optional)
              <input
                type="text"
                value={scheduleForm.location}
                onChange={(event) => setScheduleForm((state) => ({ ...state, location: event.target.value }))}
              />
            </label>
            <label>
              Notes
              <textarea
                rows={3}
                value={scheduleForm.notes}
                onChange={(event) => setScheduleForm((state) => ({ ...state, notes: event.target.value }))}
                placeholder="Agenda, dial-in details, or preparation notes"
              />
            </label>
            {scheduleMessage && <div className={`notice notice--${scheduleMessage.type}`}>{scheduleMessage.text}</div>}
            {scheduledLink && (
              <div className="muted">
                <a
                  href={scheduledLink}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="View event in Google Calendar (opens in a new tab)"
                >
                  View event in Google Calendar
                </a>
              </div>
            )}
            <div className="form-actions">
              <button className="btn btn--primary" type="submit" disabled={scheduleMeetingMutation.isPending}>
                {scheduleMeetingMutation.isPending ? 'Scheduling…' : 'Schedule meeting'}
              </button>
              <button className="btn btn--secondary" type="button" onClick={closeScheduler}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
